import angular from 'angular';

import { isValidDateObject } from '../dateutil';
import { normalizeIdentifier } from '../util';
import { RECAP_RESPONSE_STATUS_SUBMITTED, RECAP_RESPONSE_STATUS_APPROVED } from '../constants';

const moduleName = 'recaps.service.PositionSearchService';

const programFilter = {
  key: 'PROGRAM',
  getOptionInfo: position => {
    return {
      name: position.programs_name,
      id: position.programs_id,
    };
  },
  type: 'select',
};

const recapFilter = {
  key: 'RECAP',
  getOptionInfo: position => {
    const event = position && position.event;
    const recapDefinition = event && event.recapDefinition;
    const name = recapDefinition && recapDefinition.name;
    const id = recapDefinition && recapDefinition.id;
    return {
      name,
      id,
    };
  },
  type: 'select',
};

/**
 * Possible options:
 * - Not Submitted (recap doesn't exist)
 * - Submitted
 * - Approved
 * @type {{getOptionInfo: recapStatusFilter.getOptionInfo, type: string, key: string}}
 */
const recapStatusFilter = {
  key: 'RECAP_RESPONSE_STATUS',
  getOptionInfo: position => {
    let translationKey;
    if (position.recapResponse) {
      const statusId = normalizeIdentifier(position.recapResponse.status_id);
      if (statusId === normalizeIdentifier(RECAP_RESPONSE_STATUS_SUBMITTED)) {
        translationKey = 'RECAP_RESPONSE_STATUS_SUBMITTED';
      } else {
        translationKey = 'RECAP_RESPONSE_STATUS_APPROVED';
      }
    } else {
      translationKey = 'RECAP_RESPONSE_NOT_SUBMITTED';
    }
    return {
      name: translationKey,
      translationKey: translationKey,
      id: translationKey,
    };
  },
  type: 'select',
};

const chainFilter = {
  key: 'CHAIN',
  getOptionInfo: position => {
    const event = position && position.event;
    const chainName = event && event.venues_chains_name;
    return {
      name: chainName,
      id: chainName,
    };
  },
  type: 'typeahead',
};

const venueFilter = {
  key: 'VENUE_NAME',
  getOptionInfo: position => {
    return {
      name: position.venue_name,
      id: position.venue_name && position.venue_name.toUpperCase(),
    };
  },
  type: 'typeahead',
};

const addressFilter = {
  key: 'ADDRESS',
  getOptionInfo: position => {
    const event = position && position.event;
    const value = event && [event.street_address1, event.street_address2].join(' ').trim();
    return {
      name: value,
      id: value,
    };
  },
  type: 'typeahead',
};

const cityFilter = {
  key: 'CITY',
  getOptionInfo: position => {
    const event = position && position.event;
    const city = event && [event.city, event.state].join(', ').trim().replace(/,$/, '').replace(/^,/, '');
    return {
      name: city,
      id: city,
    };
  },
  type: 'typeahead',
};

export const optionFilters = {
  program: programFilter,
  recap: recapFilter,
  recapStatus: recapStatusFilter,
  chain: chainFilter,
  venue: venueFilter,
  address: addressFilter,
  city: cityFilter,
};

const getValidDateFieldsForPosition = position => {
  const possibleDateFields = [];
  const addDateIfValid = date => {
    if (isValidDateObject(date)) {
      possibleDateFields.push(date);
    }
  };
  addDateIfValid(position.start_date);
  addDateIfValid(position.end_date);
  addDateIfValid(position.event && position.event.start_timestamp);
  addDateIfValid(position.event && position.event.end_timestamp);
  return possibleDateFields;
};

const dateMatch = (startOrEnd, position, filterValue) => {
  console.debug(`dateMatch(${startOrEnd}, position=${position.id}, ${filterValue})`);
  if (!position || !filterValue || !isValidDateObject(filterValue)) {
    console.debug('missing value or invalid date object');
    return false;
  }
  const addToCutoff = startOrEnd === 'end' ? (24 * 60 * 60 * 1000) : 0;
  const cutoffDate = new Date(filterValue.getTime() + addToCutoff);
  cutoffDate.setHours(0, 0, 0, 0);
  const dateFields = getValidDateFieldsForPosition(position);
  const compareStart = date => date >= cutoffDate;
  const compareEnd = date => date <= cutoffDate;
  const result = dateFields.some(startOrEnd === 'end' ? compareEnd : compareStart);
  console.debug(`result:`, result);
  return result;
};

const startDateFilter = {
  key: 'START_DATE',
  match: (position, filterValue) => dateMatch('start', position, filterValue),
};

const endDateFilter = {
  key: 'END_DATE',
  match: (position, filterValue) => dateMatch('end', position, filterValue),
};

export const valueFilters = {
  start_date: startDateFilter,
  end_date: endDateFilter,
};

export const filters = {
  ...optionFilters,
  ...valueFilters,
};

/**
 * Create mapping of ID to position
 * @param positions
 * @return object where keys are ID and values are position
 */
const getPositionsById = positions => {
  return positions.reduce((map, position) => {
    map[position.id] = position;
    return map;
  })
};

/**
 * Given a map of filter name to filter info, and a list of positions, create mapping of filter name to all applicable options
 * @param filters Object mapping filter name to filter info (key, getOptionInfo callable)
 * @param positions Array of positions
 * @returns Object mapping filter name to result of `getApplicableOptions` (nested object)
 */
const getOptionsByFilter = (filters, positions) => {
  // console.debug(`PositionSearchService getOptionsByFilter() filters:`, filters, `positions:`, positions);
  const options = Object.keys(filters).reduce((optionsByFilter, filterName) => {
    optionsByFilter[filterName] = getApplicableOptions(filters[filterName], positions);
    return optionsByFilter;
  }, {});

  for (const filterName of Object.keys(filters)) {
    for (const optionInfo of Object.values(options[filterName])) {
      // console.debug(`PositionSearchService getOptionsByFilter() filter '${filterName}' option id ${optionInfo.id} count ${optionInfo.count}`);
    }
  }

  // console.debug(`PositionSearchService getOptionsByFilter() returning options:`, options);
  return options;
};

/**
 * Create list of applicable options for a given filter and list of positions
 * @param filter Object defining a filter, with 'key' (string) and 'getOptionInfo' (callable)
 * @param positions Array of positions
 * @return object where keys are option ID, values are themselves objects with 'name' (string) and 'positions' (array)
 */
const getApplicableOptions = (filter, positions) => {
  // console.debug(`PositionSearchService getApplicableOptions() filter:`, filter, `positions:`, positions);

  /**
   * Returns an array of option infos, calling either filter.getOptionInfos() or filter.getOptionInfo()
   * @param filter
   * @param position
   * @returns {({name: *, id: *}|{name, id}|{translationKey: string, name: string, id}|{name, id: *}|*)[]|*}
   */
  const getOptionInfos = (filter, position) => {
    if (typeof filter.getOptionInfos !== 'undefined') {
      return filter.getOptionInfos(position);
    } else {
      return [filter.getOptionInfo(position)];
    }
  };

  const options = positions.reduce((optionIdMap, position) => {
    const optionInfos = getOptionInfos(filter, position);
    optionInfos.forEach(optionInfo => {
      const { name, id, translationKey } = optionInfo;

      // Don't add options with missing ID
      if (optionInfo && optionInfo.id) {
        if (!optionIdMap[id]) {
          optionIdMap[id] = {
            id,
            name,
            translationKey,
            positions: [],
            count: 0,
          };
        }
        optionIdMap[id].positions.push(position);
        optionIdMap[id].count += 1;
      }
    });

    return optionIdMap;
  }, {});

  const count = Object.keys(options).length;
  // console.debug(`PositionSearchService getApplicableOptions() returning ${count} options:`, options);
  return options;
};

/**
 * Determine if the specified position matches the active filter options
 * @param position
 * @param activeOptionIdsByFilter Object mapping a filter name to an array of active option ID's
 * @param optionsByFilter Object mapping a filter name to an object mapping filter option ID to id, name, positions
 * @returns {boolean}
 */
const positionMatchesOptionFilters = (position, activeOptionIdsByFilter, optionsByFilter) => {
  // console.debug(`PositionSearchService positionMatchesOptionFilters()`)
  return Object.keys(activeOptionIdsByFilter).every(filterName => {
    const activeOptionIds = Object.keys(activeOptionIdsByFilter[filterName] || {});
    const optionsArray = Object.values(optionsByFilter[filterName] || {});
    const activeOptions = optionsArray.filter(option => activeOptionIds.indexOf(option.id) >= 0);
    return activeOptions.some(option => option.positions.indexOf(position) >= 0);
  });
};

/**
 * Determine if the specified position matches the active filter values
 * @param position
 * @param activeFilterValuesByFilter Object mapping a filter name to the value for that filter
 * @returns {boolean}
 */
const positionMatchesValueFilters = (position, activeFilterValuesByFilter) => {
  return Object.keys(activeFilterValuesByFilter).every(filterName => {
    const filterValue = activeFilterValuesByFilter[filterName];
    const filter = valueFilters[filterName];
    return filter.match(position, filterValue);
  });
};

/**
 * Simple case-insensitive string compare function
 * @param stringA
 * @param stringB
 * @returns {number}
 */
const defaultStringCompareFunction = (stringA, stringB) => {
  stringA = stringA && stringA.toUpperCase();
  stringB = stringB && stringB.toUpperCase();
  if (!stringA && !stringB) {
    return 0;
  } else if (stringA && !stringB) {
    return 1;
  } else if (stringB && !stringA) {
    return -1;
  } else if (stringA < stringB) {
    return -1;
  } else if (stringB < stringA) {
    return 1;
  } else {
    return 0;
  }
};

/**
 * Use case-insensitive string compare on names
 * @param filterOption1
 * @param filterOption2
 * @returns {number}
 */
const defaultFilterOptionSortCompareFunction = (filterOption1, filterOption2) => {
  return defaultStringCompareFunction(
    filterOption1 && filterOption1.name,
    filterOption2 && filterOption2.name
  );
};

/**
 * Group a list of positions into previous, current, upcoming, rejected, and available,
 * based on event date and availability status.
 * @param positions
 * @returns {{previous: Array, current: Array, upcoming: Array, rejected: Array, available: Array}}
 */
const groupPositionsByDate = positions => {
  const previous = [],
    current = [],
    upcoming = [],
    rejected = [],
    available = [];
  for (let position of positions) {
    if (position.isAvailable) {
      available.push(position);
    } else if (position.isPast || position.isCurrent) {
      // position.recapRejected, not the response status alone: the response lives in a
      // window-limited store and is frequently absent client-side, which is the reported defect -
      // a rejected recap never appeared in this bucket at all. The cost is that the event-row
      // fallback is not per-staffer (see Position.recapRejected): on a multi-staffer event
      // carrying a legacy per-event office rejection, a staffer whose own response has not synced
      // sees their shift here rather than in Previous. That is the multi-staffer case this ticket
      // leaves broken by design; the single-staffer case, which is what it fixes, is exact.
      //
      // The isPast || isCurrent partition around this branch is untouched, so a FUTURE shift whose
      // early recap (allow_before_date_days) was rejected still groups as upcoming and is absent
      // from this bucket and its count - the same as before. Its rejection is not hidden: the card
      // banner keys on position.recapRejected with no date condition, so it renders wherever the
      // shift is listed. Moving those shifts here would take them out of Upcoming, which is a
      // change to a bucket nobody reported.
      if (position.recapRejected) {
        rejected.push(position);
      } else if (position.isPast) {
        previous.push(position);
      } else if (position.isCurrent) {
        current.push(position);
      }
    } else if (position.isFuture) {
      upcoming.push(position);
    }
  }
  const grouped = {previous, current, upcoming, rejected, available};

  const getIds = l => l.map(o => o.id);
  const getIdsByGroup = groups => Object.keys(groups).reduce((idsByGroup, groupName) => {
    idsByGroup[groupName] = getIds(groups[groupName]);
    return idsByGroup;
  }, {});

  console.debug(`groupPositionsByDate() positions:`, getIds(positions), "grouped:", getIdsByGroup(grouped));

  return grouped;
};

/**
 * Group positions by completion status for the merchandising activity view:
 * incomplete (assigned, no submitted/approved recap yet) vs complete (recap
 * submitted or approved). Rejected and available positions are intentionally
 * left out — the merchandising panels reuse the rejected/available groups from
 * groupPositionsByDate — so a position still lands in exactly one bucket.
 * @param positions
 * @returns {{incomplete: Array, complete: Array}}
 */
const groupPositionsByStatus = positions => {
  const incomplete = [], complete = [];
  for (let position of positions) {
    if (position.isAvailable) {
      continue; // shown in the Available bucket
    }
    // Only past and current shifts reach the Rejected bucket in groupPositionsByDate(), so
    // skipping a future one here would leave it in no bucket at all.
    if (position.recapRejected && (position.isPast || position.isCurrent)) {
      continue; // shown in the Rejected bucket
    }
    const statusId = position.recapResponse && String(position.recapResponse.status_id);
    // Complete when the recap is done. Prefer the recap response status when it
    // is present, but fall back to the event's own status (Recapped/Finished).
    // The recap response is synced in a separate, window-limited store and is
    // frequently absent client-side, whereas the event status is embedded on the
    // position - so relying on the response alone dropped finished bookings into
    // Incomplete. The two signals are kept in sync server-side, so this only
    // recovers the reliable copy of the same truth.
    const responseDone = statusId === String(RECAP_RESPONSE_STATUS_SUBMITTED)
      || statusId === String(RECAP_RESPONSE_STATUS_APPROVED);
    const eventDone = position.event && position.event.isRecapDone();
    if (responseDone || eventDone) {
      complete.push(position);
    } else {
      incomplete.push(position);
    }
  }
  return { incomplete, complete };
};

/**
 * Combine the date-based groups (legacy dashboard) with the status-based
 * incomplete/complete groups (merchandising dashboard) into one object, so the
 * same grouped result serves both layouts; the panels component renders the
 * subset appropriate to the active mode.
 * @param positions
 */
const groupPositions = positions => {
  return {
    ...groupPositionsByDate(positions),
    ...groupPositionsByStatus(positions),
  };
};

/**
 * Sort positions by start_date
 * @param positions
 * @returns sorted positions
 */
const sortPositionsByDate = (positions) => positions.sort((p1, p2) => p1.start_time - p2.start_time);

/**
 * Sort positions by soonest recap expiration date (ascending). Positions with
 * no/invalid expiration date sort last, so the ones a merchandiser must finish
 * first float to the top of the Incomplete bucket.
 * @param positions
 * @returns sorted positions
 */
const sortPositionsByExpiration = (positions) => {
  const expirationTime = position => {
    const exp = position && position.event && position.event.expiration_date;
    const time = exp ? new Date(exp).getTime() : NaN;
    return Number.isNaN(time) ? Infinity : time;
  };
  return positions.sort((p1, p2) => expirationTime(p1) - expirationTime(p2));
};

/**
 * Sort grouped positions. Previous/complete positions are sorted in descending
 * order (most recent first); incomplete positions by soonest expiration.
 * @param groupedPositions
 * @returns {{previous, current, upcoming, rejected, available, incomplete, complete}}
 */
const sortGroupedPositions = groupedPositions => {
  const {rejected, previous, current, upcoming, available, incomplete, complete} = groupedPositions;
  const sortedGroupedPositions = {
    rejected: sortPositionsByDate(rejected),
    previous: sortPositionsByDate(previous),
    current: sortPositionsByDate(current),
    upcoming: sortPositionsByDate(upcoming),
    available: sortPositionsByDate(available),
    incomplete: sortPositionsByExpiration(incomplete || []),
    complete: sortPositionsByDate(complete || []),
  };
  sortedGroupedPositions.previous.reverse();
  sortedGroupedPositions.complete.reverse();
  return sortedGroupedPositions;
};

const countGroupedArrays = groups => {
  return Object.keys(groups).reduce((countByGroup, key) => {
    countByGroup[key] = groups[key] && groups[key].length || 0;
    return countByGroup;
  }, {});
};

class PositionSearchService {
  constructor($q, LogService, APP_CONFIG) {
    this.$q = $q;
    this.LogService = LogService;
    this.APP_CONFIG = APP_CONFIG;

    // Master array of positions
    this._allPositions = null;
    this._allPositionsByGroup = null;
    this._allPositionsCountByGroup = null;

    // Array of positions with current filters (updated immediately)
    this._filteredPositions = null;
    this._filteredPositionsByGroup = null;
    this._filteredPositionsCountByGroup = null;

    // Array of positions from last "applied" filters (only updated when filters are applied)
    this._appliedFilteredPositions = null;
    this._appliedFilteredPositionsByGroup = null;
    this._appliedFilteredPositionsCountByGroup = null;

    this._optionsByFilterForFilteredPositions = null;
    this._optionsByFilterForAllPositions = null;

    this._anyAppliedFiltersActive = false;

    this._venueCustomSearchFields = [];

    // Reset filters to initialize all filter variables
    this.resetFilters();
  }

  get filterDefinitions() {
    return this._optionFilterDefinitions;
  }

  getFilterDefinitionByName(filterName) {
    return filters[filterName];
  }

  set allPositions(allPositions) {
    this.LogService && this.LogService.debug(`PositionSearchService set allPositions:`, allPositions);
    this._allPositions = allPositions;
    this._optionsByFilterForAllPositions = null;
    this._markFiltersDirty();
    this._markAppliedFiltersDirty();

    // Update counts
    this._allPositionsByGroup = sortGroupedPositions(groupPositions(allPositions));
    this._allPositionsCountByGroup = countGroupedArrays(this._allPositionsByGroup);
  }

  get allPositions() {
    return this._allPositions;
  }

  get allPositionsByGroup() {
    return this._allPositionsByGroup;
  }

  get allPositionsCountByGroup() {
    return this._allPositionsCountByGroup;
  }

  get filteredPositions() {
    this._ensureFiltersUpdated();
    return this._filteredPositions;
  }

  get filteredPositionsByGroup() {
    this._ensureFiltersUpdated();
    return this._filteredPositionsByGroup;
  }

  get filteredPositionsCountByGroup() {
    this._ensureFiltersUpdated();
    return this._filteredPositionsCountByGroup;
  }

  get appliedFilteredPositions() {
    this._ensureAppliedFiltersUpdated();
    return this._appliedFilteredPositions || this.allPositions;
  }

  get appliedFilteredPositionsByGroup() {
    this._ensureAppliedFiltersUpdated();
    return this._appliedFilteredPositionsByGroup || this.allPositionsByGroup;
  }

  get appliedFilteredPositionsCountByGroup() {
    this._ensureAppliedFiltersUpdated();
    return this._appliedFilteredPositionsCountByGroup || this.allPositionsCountByGroup;
  }

  get anyFiltersActive() {
    const optionFiltersActive = this._activeOptionIdsByFilter
      && Object.values(this._activeOptionIdsByFilter).some(optionIds => optionIds && Object.keys(optionIds).length > 0);
    const valueFiltersActive = this._activeValuesByFilter && Object.keys(this._activeValuesByFilter).length > 0;
    return optionFiltersActive || valueFiltersActive;
  }

  get anyAppliedFiltersActive() {
    return this._anyAppliedFiltersActive;
  }

  set venueCustomSearchFields(fields) {
    this.LogService.debug(`PositionSearchSErvice setting venueCustomSearchFields:`, fields);
    this._venueCustomSearchFields = fields;
    this._markFiltersDirty();
  }

  get venueCustomSearchFields() {
    return this._venueCustomSearchFields;
  }

  clearAllData() {
    this.resetFilters();
    this.allPositions = [];
  }

  resetFilters() {
    this.LogService && this.LogService.debug(`PositionSearchService.resetFilters()`);

    // Reset active filters
    this._activeOptionIdsByFilter = {};
    this._activeValuesByFilter = {};

    // Reset filtered positions; should match all positions
    this._filteredPositions = null;
    this._filteredPositionsByGroup = null;
    this._filteredPositionsCountByGroup = null;

    // Reset options for filtered positions
    this._optionsByFilterForFilteredPositions = null;

    this._updateFilterDefinitions();
  }

  resetFiltersAndApply() {
    this.resetFilters();
    this.applyFilters();
  }

  addFilterOption(filterName, filterOptionId, filterOptionData = null) {
    this.LogService && this.LogService.debug(`PositionSearchService.addFilterOption(${filterName}, ${filterOptionId})`);

    if (!this._activeOptionIdsByFilter[filterName]) {
      this._activeOptionIdsByFilter[filterName] = {};
    }
    this._activeOptionIdsByFilter[filterName][filterOptionId] = {
      id: filterOptionId,
      data: filterOptionData,
    };
    this._markFiltersDirty();
  }

  removeFilterOption(filterName, filterOptionId = null) {
    this.LogService && this.LogService.debug(`PositionSearchService.removeOptionFilter(${filterName}, ${filterOptionId})`);

    if (!this._activeOptionIdsByFilter[filterName]) {
      return;
    }
    if (!filterOptionId) {
      delete this._activeOptionIdsByFilter[filterName];
    } else if (this._activeOptionIdsByFilter[filterName][filterOptionId]) {
      delete this._activeOptionIdsByFilter[filterName][filterOptionId];
    }
    this._markFiltersDirty();
  }

  addFilterValue(filterName, value) {
    this.LogService && this.LogService.debug(`PositionSearchService.addFilterOption(${filterName}, ${value})`);
    if (!this._activeValuesByFilter) {
      this._activeValuesByFilter = {};
    }
    this._activeValuesByFilter[filterName] = value;
    this._markFiltersDirty();
  }

  removeFilterValue(filterName) {
    this.LogService && this.LogService.debug(`PositionSearchService.removeFilterValue(${filterName})`);
    if (this._activeValuesByFilter && this._activeValuesByFilter[filterName] !== undefined) {
      delete this._activeValuesByFilter[filterName];
    }
    this._markFiltersDirty();
  }

  /**
   * Retrieve currently active filter options, as an object mapping filter name to an array of active options
   * {
   *   filterName: {
   *     filterOptionId: [
   *       filterOption,
   *       filterOption,
   *     ]
   *   }
   * }
   */
  get activeFilterOptionsByFilter() {
    return this._activeOptionIdsByFilter;
  }

  /**
   * Retrieve currently active filter values, as an object mapping filter name to value
   * {
   *   filterName: filterValue
   * }
   */
  get activeFilterValuesByFilter() {
    return this._activeValuesByFilter;
  }

  /**
   * Copy data from working filters to applied filters
   */
  applyFilters() {
    this.LogService && this.LogService.debug(`PositionSearchService.applyFilters()`)
    this._appliedFilteredPositions = [...this.filteredPositions];
    this._appliedFilteredPositionsByGroup = sortGroupedPositions(groupPositions(this._appliedFilteredPositions));
    this._appliedFilteredPositionsCountByGroup = countGroupedArrays(this._appliedFilteredPositionsByGroup);
    this._anyAppliedFiltersActive = this.anyFiltersActive;
    this._appliedFiltersDirty = false;
  }

  /**
   * Return sorted array of available filter options for given filter
   * @param filterName
   * @returns {*}
   */
  availableOptionsForFilter(filterName) {
    if (!this.LogService) {
      return [];
    }

    this._ensureFiltersUpdated();

    if (!this._optionsArrayByFilterForFilteredPositions) {
      this._optionsArrayByFilterForFilteredPositions = {};
    }

    if (!this._optionsArrayByFilterForFilteredPositions[filterName]) {
      const filterOptionMap = this._optionsByFilterForFilteredPositions[filterName];
      if (!filterOptionMap) {
        return null;
      }
      const filterOptions = Object.values(filterOptionMap);
      filterOptions.sort(defaultFilterOptionSortCompareFunction);
      this._optionsArrayByFilterForFilteredPositions[filterName] = filterOptions;
    }

    return this._optionsArrayByFilterForFilteredPositions[filterName];
  }

  findOptionByOptionName(filterName, optionName) {
    this._ensureFiltersUpdated();
    const allOptionsById = this._optionsByFilterForAllPositions[filterName] || {};
    const foundOptionId = Object.keys(allOptionsById).find(optionId => {
      const currOption = allOptionsById[optionId];
      return defaultStringCompareFunction(currOption.name, optionName) === 0;
    });
    if (foundOptionId !== undefined) {
      return allOptionsById[foundOptionId];
    } else {
      return undefined;
    }
  }

  findOptionIdForOptionName(filterName, optionName) {
    const foundOption = this.findOptionByOptionName(filterName, optionName);
    return foundOption && foundOption.id;
  }

  _markFiltersDirty() {
    this.LogService && this.LogService.debug(`PositionSearchService._markFiltersDirty()`);
    this._filtersDirty = true;
    this._filteredPositions = null;
    this._optionsByFilterForFilteredPositions = null;
    this._optionsArrayByFilterForFilteredPositions = {};
  }

  _markAppliedFiltersDirty() {
    this._appliedFiltersDirty = true;
  }

  _ensureFiltersUpdated() {
    if (this._filtersDirty || !this._filteredPositions || !this._optionsByFilterForFilteredPositions) {
      this.LogService && this.LogService.debug(`PositionSearchService._ensureFiltersUpdated() will call _updateFilters()`);
      return this._updateFilters();
    }
  }

  _ensureAppliedFiltersUpdated() {
    if (this._appliedFiltersDirty) {
      this.LogService && this.LogService.debug(`PositionSearchService._ensureAppliedFiltersUpdated() will call applyFilters()`);
      this.applyFilters();
    }
  }

  _updateFilters() {
    // Step 0: Start with list of all positions
    const allPositions = this.allPositions;

    // Step 0a: Update available filters to include custom filters
    this._updateFilterDefinitions();
    const optionFilters = this._optionFilterDefinitions;

    // Step 1: Apply value filters (date range)
    const activeFilterValuesByFilter = this.activeFilterValuesByFilter;
    const valueFilteredPositions = allPositions.filter(position => positionMatchesValueFilters(position, activeFilterValuesByFilter));

    // Step 2: Apply option filters
    const activeOptionIdsByFilter = this._activeOptionIdsByFilter;

    // Step 2a: Compute list of options from all positions, if it doesn't exist.
    // This gives us a mapping, for each option, which positions match it (from list of all positions)

    /**
     * MJOL-1213
     * this._optionsByFilterForAllPositions doesn't get reset when setting allPositions..
     * So we'll disable this for now, and force a recompute.
     */
    this._optionsByFilterForAllPositions = null;

    const optionsByFilterForAllPositions = this._optionsByFilterForAllPositions || getOptionsByFilter(optionFilters, allPositions);

    // Step 2b: Apply active option filters

    const valueAndOptionFilteredPositions = valueFilteredPositions.filter(position => positionMatchesOptionFilters(position, activeOptionIdsByFilter, optionsByFilterForAllPositions));

    // Step 3: Tabulate filter option data
    // For each filter, tabulate filter options as if no filters of this category were selected. This avoids the problem
    // of only showing a single option for each filter that is currently applied.

    const optionsByFilterForFilteredPositions = Object.keys(optionFilters).reduce((optionsByFilter, filterName) => {
      const filter = optionFilters[filterName];
      const activeOptionIdsByFilterWithoutCurrentFilter = {...activeOptionIdsByFilter};
      delete activeOptionIdsByFilterWithoutCurrentFilter[filterName];
      const filteredPositionsWithoutCurrentFilter = valueFilteredPositions.filter(position => positionMatchesOptionFilters(position, activeOptionIdsByFilterWithoutCurrentFilter, optionsByFilterForAllPositions));
      optionsByFilter[filterName] = getApplicableOptions(filter, filteredPositionsWithoutCurrentFilter);
      return optionsByFilter;
    }, {});

    // Step 4: Ensure that any currently active filters remain in the optionsByFilter array so they are not hidden

    const optionsByFilterForFilteredPositionsIncludingActiveOptions = Object.keys(optionFilters).reduce((optionsByFilter, filterName) => {

      // Copy data from optionsByFilterForFilteredPositions
      if (optionsByFilterForFilteredPositions[filterName]) {
        optionsByFilter[filterName] = {...optionsByFilterForFilteredPositions[filterName]};
      }

      // Check if there are active filter option values for this filter, and if so, ensure they are also present
      const activeOptionIds = activeOptionIdsByFilter[filterName];
      if (activeOptionIds) {
        if (!optionsByFilter[filterName]) {
          optionsByFilter[filterName] = {};
        }
        Object.keys(activeOptionIds).forEach(activeOptionId => {
          if (optionsByFilter[filterName][activeOptionId] === undefined) {
            // Add this option, from the optionsByFilterForAllPositions data
            optionsByFilter[filterName][activeOptionId] = optionsByFilterForAllPositions[filterName][activeOptionId];
          }
        });
      }

      return optionsByFilter;
    }, {});

    // Step 5: Update counts
    const filteredPositions = valueAndOptionFilteredPositions;
    const filteredPositionsByGroup = sortGroupedPositions(groupPositions(filteredPositions));
    const filteredPositionsCountByGroup = countGroupedArrays(filteredPositionsByGroup);

    // Step 6: Write all member data
    this._filteredPositions = filteredPositions;
    this._filteredPositionsByGroup = filteredPositionsByGroup;
    this._filteredPositionsCountByGroup = filteredPositionsCountByGroup;
    this._optionsByFilterForAllPositions = optionsByFilterForAllPositions;
    this._optionsByFilterForFilteredPositions = optionsByFilterForFilteredPositionsIncludingActiveOptions;
    this._optionsArrayByFilterForFilteredPositions = {};

    // Finally mark filters as not dirty
    this._filtersDirty = false;
  }

  /**
   * Updates all filter definitions, including statically defined and dynamic from venue custom fields
   * @returns {*}
   * @private
   */
  _updateFilterDefinitions() {
    const staticOptionFilters = optionFilters;
    const staticValueFilters = valueFilters;

    const venueCustomFilters = this._buildVenueCustomFilters();
    const dynamicOptionFilters = venueCustomFilters.optionFilters;
    const dynamicValueFilters = venueCustomFilters.valueFilters;

    const allOptionFilters = {
      ...staticOptionFilters,
      ...dynamicOptionFilters,
    };
    const allValueFilters = {
      ...staticValueFilters,
      ...dynamicValueFilters,
    };

    // Array of filter definitions
    const optionFilterDefinitionList = Object.keys(allOptionFilters)
      .map(filterName => {
        return {
          name: filterName,
          ...allOptionFilters[filterName],
        };
      })
      .filter(this._shouldFilterByEnabled)
    ;
    const valueFilterDefinitionList = Object.keys(allValueFilters)
      .map(filterName => {
        return {
          name: filterName,
          ...allValueFilters[filterName],
        };
      })
      .filter(this._shouldFilterByEnabled)
    ;

    const allFilterDefinitionsList = [
      ...optionFilterDefinitionList,
      ...valueFilterDefinitionList,
    ];

    const mapByField = (l, f) => l.reduce((pre, cur) => {
      pre[cur[f]] = cur;
      return pre;
    }, {});
    const mapByName = l => mapByField(l, 'name');

    const allFilterDefinitions = mapByName(allFilterDefinitionsList);
    const optionFilterDefinitions = mapByName(optionFilterDefinitionList);
    const valueFilterDefinitions = mapByName(valueFilterDefinitionList);

    this._allFilterDefinitions = allFilterDefinitions;
    this._optionFilterDefinitions = optionFilterDefinitions;
    this._valueFilterDefinitions = valueFilterDefinitions;

    return allFilterDefinitions;
  }

  _buildVenueCustomFilters() {
    let venueCustomSearchFields = this.venueCustomSearchFields;
    if (!venueCustomSearchFields) {
      this.LogService.error(`Didn't get venue custom search fields!`);
      venueCustomSearchFields = [];
    }

    const selectFieldTypes = ['select', 'multiselect'];
    const filterTypeForSearchField = searchField => {
      if (selectFieldTypes.includes(searchField.field_type)) {
        return 'select';
      } else {
        return 'typeahead';
      }
    };

    const venueCustomFilters = venueCustomSearchFields.reduce((filters, searchField) => {
      const fieldName = searchField.name;
      const filterName = `venues_custom_${searchField.id}`;
      const filterTranslationKey = searchField.name.toUpperCase();
      const type = filterTypeForSearchField(searchField);
      filters[filterName] = {
        key: filterTranslationKey,
        name: filterName,
        type: type,
        getOptionInfos: position => {
          const optionInfos = [];
          const event = position && position.event;
          const eventVenueCustomFieldsByName = event && event.venue_custom_fields_by_name;
          const value = eventVenueCustomFieldsByName && eventVenueCustomFieldsByName[fieldName];
          if (type === 'select') {
            // Find the venues_custom entry on this position that matches this search field
            // (note that we're pulling this from a different location; this is where the position display modal venue custom fields are stored)
            const positionVenuesCustomFields = position.venues_custom || [];
            const positionVenuesCustomField = positionVenuesCustomFields.find(venuesCustomField => {
              return venuesCustomField.name && venuesCustomField.name === fieldName;
            });
            const selectedOptions = positionVenuesCustomField && positionVenuesCustomField.selected_options || [];
            selectedOptions.forEach(selectedOption => {
              // selectedOption has id, name, display_order
              optionInfos.push({
                name: selectedOption.name,
                id: `venues_custom_option_${selectedOption.id}`,
              });
            });
          } else {
            optionInfos.push({
              name: value,
              id: value,
            })
          }
          return optionInfos;
        },
      };
      return filters;
    }, {});

    return {
      optionFilters: venueCustomFilters,
      valueFilters: {},
    };
  }

  /**
   * Runtime check to see which filters should be enabled.
   * Used to disable Mosaic-specific filters before we have a more robust solution.
   * @param filterInfo
   * @returns {boolean}
   */
  _shouldFilterByEnabled(filterInfo) {
    if (filterInfo && filterInfo.isMosaicField && !APP_CONFIG.isMosaic) {
      return false;
    }
    return true;
  }
}

const module = angular.module(moduleName, []);
module.service('PositionSearchService', PositionSearchService);

export default module;
